import { BookingPricingType } from '@/types';

/** Booking times are fixed to IST (server: Booking.timezone), whatever the viewer's clock says. */
const TIME_ZONE = 'Asia/Kolkata';

interface BookingWhenInput {
  travelDate: string;
  startAt?: string;
  endAt?: string;
  pricingType?: BookingPricingType;
  duration?: number;
}

const dayFormatter = new Intl.DateTimeFormat('en-IN', {
  timeZone: TIME_ZONE,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const shortDayFormatter = new Intl.DateTimeFormat('en-IN', {
  timeZone: TIME_ZONE,
  day: 'numeric',
  month: 'short',
});

const timeFormatter = new Intl.DateTimeFormat('en-IN', {
  timeZone: TIME_ZONE,
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

/** Legacy bookings: travel date is UTC midnight of the calendar day. */
const legacyDayFormatter = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

function istDay(iso: string) {
  return new Date(new Date(iso).getTime() + 5.5 * 3_600_000).toISOString().slice(0, 10);
}

function formatTime(iso: string) {
  return timeFormatter.format(new Date(iso)).toUpperCase();
}

/** A single IST clock time, e.g. "4:00 PM". */
export function formatBookingClock(iso: string): string {
  return formatTime(iso);
}

/** "Fri, 30 Oct 2026" or "30 Oct – Sun, 1 Nov 2026" for multi-day trips. */
export function formatBookingDate(booking: BookingWhenInput): string {
  if (!booking.startAt || !booking.endAt) {
    const date = new Date(booking.travelDate);
    return Number.isNaN(date.getTime()) ? booking.travelDate : legacyDayFormatter.format(date);
  }
  if (istDay(booking.startAt) === istDay(booking.endAt)) {
    return dayFormatter.format(new Date(booking.startAt));
  }
  return `${shortDayFormatter.format(new Date(booking.startAt))} – ${dayFormatter.format(new Date(booking.endAt))}`;
}

/** "4:00 PM – 6:00 PM", or "9:00 AM – 6:00 PM daily" for multi-day trips; empty for legacy rows. */
export function formatBookingTime(booking: BookingWhenInput): string {
  if (!booking.startAt || !booking.endAt) return '';
  const range = `${formatTime(booking.startAt)} – ${formatTime(booking.endAt)}`;
  return istDay(booking.startAt) === istDay(booking.endAt) ? range : `${range} daily`;
}

/** Full label, e.g. "Fri, 30 Oct 2026 · 4:00 PM – 6:00 PM". */
export function formatBookingWhen(booking: BookingWhenInput): string {
  const time = formatBookingTime(booking);
  return time ? `${formatBookingDate(booking)} · ${time}` : formatBookingDate(booking);
}

/** "2 hours", "1 hour", "3 days", "Full day". */
export function formatBookingDuration(booking: BookingWhenInput): string {
  const duration = booking.duration ?? 1;
  if (booking.pricingType === BookingPricingType.FULL_DAY) {
    return duration > 1 ? `${duration} days` : 'Full day';
  }
  return duration === 1 ? '1 hour' : `${duration} hours`;
}
